import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { isAdSenseEnabled, PUBLISHER_ID } from "../lib/adsense.js";

/* ============================================================
   Cookie / consent management
   ============================================================
   Brazilian LGPD compliance + AdSense requirement.
   
   States:
   - 'unset': user has not made a choice — show banner
   - 'accepted': loads GA and AdSense scripts
   - 'rejected': site works normally but no tracking/ads
   
   We DO load the AdSense + GA scripts on consent (not before).
   Theme preference and FX cache use localStorage which is 
   "strictly necessary" under LGPD and exempt from consent.
   ============================================================ */

const STORAGE_KEY = "anj-consent";
const ConsentContext = createContext(null);

function readConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "accepted" || raw === "rejected") return raw;
  } catch (_) {
    // ignore
  }
  return "unset";
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(readConsent);

  const update = useCallback((value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {}
    setConsent(value);
  }, []);

  // Inject AdSense script on accept (only if a publisher ID is configured)
  useEffect(() => {
    if (consent !== "accepted") return;
    if (!isAdSenseEnabled()) return;
    const existing = document.querySelector(
      'script[src*="pagead2.googlesyndication.com"]'
    );
    if (existing) return;
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUBLISHER_ID}`;
    document.head.appendChild(script);
  }, [consent]);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        hasConsented: consent === "accepted",
        accept: () => update("accepted"),
        reject: () => update("rejected"),
      }}
    >
      {children}
      <ConsentBanner />
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside ConsentProvider");
  return ctx;
}

function ConsentBanner() {
  const { consent, accept, reject } = useConsent();

  if (consent !== "unset") return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 md:bottom-4 md:left-4 md:right-auto md:max-w-md"
      role="dialog"
      aria-labelledby="cookie-title"
    >
      <div className="bg-surface border-t md:border border-border md:rounded-2xl shadow-2xl p-5 md:p-6 mx-0 md:mx-0">
        <div className="flex items-start gap-3 mb-3">
          <Cookie size={20} className="text-accent shrink-0 mt-0.5" />
          <div>
            <h3
              id="cookie-title"
              className="font-serif font-medium text-lg leading-tight mb-1"
            >
              Cookies e privacidade
            </h3>
            <p className="text-[13px] text-ink-soft leading-relaxed">
              Usamos cookies para entender o uso do site e mostrar publicidade
              relevante. Você pode aceitar ou recusar — o site funciona dos
              dois jeitos.{" "}
              <Link
                to="/privacidade"
                className="text-accent underline hover:no-underline"
              >
                Saber mais
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={accept}
            className="flex-1 bg-ink text-bg py-2.5 px-4 rounded-lg text-sm font-semibold hover:bg-ink/90 transition-colors"
          >
            Aceitar
          </button>
          <button
            onClick={reject}
            className="flex-1 bg-transparent text-ink py-2.5 px-4 rounded-lg text-sm font-medium border border-border hover:bg-border/40 transition-colors"
          >
            Recusar
          </button>
        </div>
      </div>
    </div>
  );
}
